/**
 * @module root/shared
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */
// core
import {
  SimpleChanges,
  OnInit,
  OnChanges,
  Component,
  ChangeDetectionStrategy,
  Input,
  OnDestroy,
  HostListener,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, Router, UrlSegment } from '@angular/router';
import { Subject } from 'rxjs';
// service
import { MarketingService } from '@root/shared/services/marketing.service';
import { WindowRefService } from '@support/shared/services/window-ref.service';
import { ContentPathsService } from '@core/aem/services/content-paths.service';
import { PdlService } from '@experience/reb-pdl/services/pdl.service';
import { MarketingDataEmitterInterface } from '@root/shared/interfaces/marketing.interface';
import { RepdashWindowRef } from '@experience/repdash/interfaces/repdash.interface';
// constant
import { CONTENT_ENDPOINT } from '@core/aem/content-endpoints';
import { SHARED_CONSTANTS } from '@root/shared/constants/shared-constants.constants';
import { PDLBaseComponent } from '@experience/reb-pdl/components/pdl.base.component';
import { takeUntil } from 'rxjs/operators';
import { CommonUtilService } from '@support/shared/services/common-util.service';
import { ConfigData } from '@experience/repdash/interfaces/aem-content.interface';
import { PdpDataHandlerService } from '@root/shared/services/pdp-data-handler.service';
import { PDL_EVENT_CTA_ID } from '@experience/reb-pdl/constants/pdl.constants';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { AppConfig } from '@root/app.config';

@Component({
  selector: 'tmo-marketing',
  templateUrl: './marketing.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MarketingComponent extends PDLBaseComponent implements OnInit, OnChanges, OnDestroy {
  @Input()
  public data: MarketingDataEmitterInterface;
  public repDashwindow: RepdashWindowRef;
  /**
   * Maintains the list of JS urls loaded by the component
   */
  public loadedJSUrls: string[];

  public eventData: object;

  private ngUnsubscribe: Subject<void>;

  // private isWebChannel: boolean;
  private pdpEventTriggered: boolean;
  constructor(
    public sanitizer: DomSanitizer,
    private marketingService: MarketingService,
    private router: Router,
    private contentPathService: ContentPathsService,
    private activatedRoute: ActivatedRoute,
    private windowRefService: WindowRefService,
    private commonUtilService: CommonUtilService,
    public routeSnapshot: ActivatedRoute,
    public pdlService: PdlService,
    private pdpHandlerService: PdpDataHandlerService,
    private stateStoreUtilsService: StateStoreUtilsService,
    private appConfig: AppConfig
  ) {
    super(pdlService, routeSnapshot);
    this.ngUnsubscribe = new Subject<void>();
    // this.isWebChannel = this.commonUtilService.isWebChannel();
    this.pdpEventTriggered = false;
    this.activatedRoute.url.pipe(takeUntil(this.ngUnsubscribe)).subscribe((url: UrlSegment[]) => {
      if (this.router && this.router.url) {
        const aemPath = this.router.url.split('/');
        const absolutePath =
          aemPath[1] && aemPath[1].split('?').length ? aemPath[1].split('?')[0] : '';
        if (aemPath && aemPath.length > 1 && aemPath[1].length > 0) {
          if (absolutePath !== CONTENT_ENDPOINT.HOME) {
            this.contentPathService.getPath(absolutePath).then(
              (path: string) => {
                this.marketingService
                  .getContentData()
                  .pipe(takeUntil(this.ngUnsubscribe))
                  .subscribe(
                    (data: ConfigData) => {
                      // if (this.isWebChannel) {
                      this.pdlService.triggerCustomEvent(this.pdlPageComponentId, 'pageLoad');
                      // }
                    },
                    () => {
                      this.router.navigate([SHARED_CONSTANTS.ERROR_404_ROUTE]);
                    }
                  );
              },
              (error: string) => {
                this.router.navigate([SHARED_CONSTANTS.ERROR_404_ROUTE]);
              }
            );
          } else if (this.router.navigated) {
            this.navigateHome();
          }
        } else if (this.router.navigated) {
          this.navigateHome();
        }
      }
    });
  }

  private navigateHome(): void {
    const brand = this.stateStoreUtilsService.getBrand();
    const route = this.appConfig.getConfig(`${brand}_path`) || '/';
    if (route.match(new RegExp('https://', 'i'))) {
      window.location.href = route;
    } else {
      this.router.navigate([route]);
    }
  }

  /**
   * Initializer lifecycle method
   */
  public ngOnInit(): void {
    super.ngOnInit();
    this.loadedJSUrls = [];
    this.repDashwindow = this.windowRefService.nativeWindow();
    if (!this.data) {
      this.data = {
        show: false,
      };
    }
    this.repDashwindow.addEventListener('scroll', () => {
      this.scrollPromptStatus();
    });
  }
  /**
   * Method to hide scroll prompt icon
   */
  public scrollPromptStatus(): void {
    const pageHeight = document.documentElement.scrollHeight;
    const scrolledPage = window.pageYOffset;
    const windowHeight = window.innerHeight;
    const footerHeight = document.getElementsByTagName('footer')[0].clientHeight;
    const scrollPromptElement = document.querySelector('.scroll-prompt');
    if (scrollPromptElement) {
      if (scrolledPage > pageHeight - windowHeight - footerHeight) {
        scrollPromptElement.setAttribute('hidden', 'true');
      } else {
        if (scrollPromptElement.hasAttribute('hidden')) {
          scrollPromptElement.removeAttribute('hidden');
        }
      }
    }
  }

  /**
   * If marketing content ,uses the associated html and js from AEM
   * @param changes
   */
  public ngOnChanges(changes: SimpleChanges): void {
    const newScripts = this.getNewScriptsToLoad(changes);
    if (newScripts && newScripts.length > 0) {
      newScripts.forEach((script: string) => {
        this.loadScript(script);
      });
    }
  }

  /**
   *
   */
  public ngOnDestroy(): void {
    super.ngOnDestroy();
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Helper method to load scripts
   * @param url
   */
  private loadScript(url: string): void {
    if (url) {
      const headTag = document.getElementsByTagName('body')[0];
      const scriptTag = document.createElement('script');
      scriptTag.setAttribute('type', 'text/javascript');
      scriptTag.src = url;
      scriptTag.onload = (e: Event) => {
        // tslint:disable-next-line:no-inferred-empty-object-type
        window.dispatchEvent(new CustomEvent('marketingContentLoaded'));
      };
      headTag.appendChild(scriptTag);
    }
  }

  /**
   * Helper method to iterate thru the AEM returned js links
   * @param changes
   */
  private getNewScriptsToLoad(changes: SimpleChanges): string[] {
    const retval = [];
    const curData = changes.data.currentValue.data;
    if (curData && curData.jsUrls && Array.isArray(curData.jsUrls)) {
      if (!this.loadedJSUrls) {
        this.loadedJSUrls = [];
      }
      curData.jsUrls.forEach((jsUrl: string) => {
        if (this.loadedJSUrls.indexOf(jsUrl) < 0) {
          this.loadedJSUrls.push(jsUrl);
        }
        retval.push(jsUrl);
      });
    }
    return retval;
  }

  /**
   * Dummy implementation for enforcing AEM component interface
   * @param data
   */
  public setData(data: MarketingDataEmitterInterface): void {}

  /**
   * Event listener for marketing navigate
   * @param event
   * @param url
   */
  @HostListener('document:marketingNavigate', ['$event', '$event.detail.url'])
  public marketingNavigate(event: Event, url: string): void {
    if (url) {
      this.commonUtilService.routeToGivenUrl(url, false);
    }
  }

  /**
   * Event listener for marketing Page PDL click Events
   * @param event
   * @param number
   * @param ctaName
   * @param href
   */

  @HostListener('document:callEvent', [
    '$event',
    '$event.detail.number',
    '$event.detail.ctaName',
    '$event.detail.href',
  ])
  public onClick(event: Event, tfn: string, ctaName: string, href: string): void {
    event.stopImmediatePropagation();
    // if (this.isWebChannel) {
    this.eventData = this.pdpHandlerService.getTriggerClickToCallEventData(tfn, ctaName, href);
    this.pdlService.triggerCustomEvent(this.pdlPageComponentId, PDL_EVENT_CTA_ID.CLICK);
    // }
  }
}
